import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBars, 
  faTimes, 
  faShoppingCart, 
  faSearch,
  faChevronDown,
  faUser,
  faSignOutAlt,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faTiktok 
} from '@fortawesome/free-brands-svg-icons';
import { useCart } from '../context/CartContext';
import AuthService from '../utils/authService';
import { Product } from '../types/product';
import { fetchProducts, getSubscriptionProducts, getProductsByCategory } from '../utils/products';

const useMobileDetect = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return isMobile;
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [user, setUser] = useState(AuthService.getUser());
  const [products, setProducts] = useState<Product[]>([]);
  const [subscriptionProducts, setSubscriptionProducts] = useState<Product[]>([]);
  const [productsByCategory, setProductsByCategory] = useState<Record<string, Product[]>>({});
  const location = useLocation();
  const navigate = useNavigate();
  const { cart } = useCart();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const userDropdownButtonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useMobileDetect();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const checkUser = () => {
      setUser(AuthService.getUser());
    };
    checkUser();
  }, [location]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const fetchedProducts = await fetchProducts();
        setProducts(fetchedProducts);
        const subscriptions = getSubscriptionProducts(fetchedProducts);
        setSubscriptionProducts(subscriptions);
        const categoryGroups: Record<string, Product[]> = {};
        fetchedProducts.forEach(product => {
          const category = product.metadata?.category || 'other';
          if (!categoryGroups[category]) {
            categoryGroups[category] = [];
          }
          categoryGroups[category].push(product);
        });
        setProductsByCategory(categoryGroups);
        
      } catch (error) {
        console.error('Error loading products in header:', error);
        // Set empty arrays to prevent UI issues
        setProducts([]);
        setSubscriptionProducts([]);
        setProductsByCategory({});
      }
    };

    loadProducts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isMobile && 
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        dropdownButtonRef.current && 
        !dropdownButtonRef.current.contains(event.target as Node)
      ) {
        setIsProductDropdownOpen(false);
      }
      
      if (!isMobile && 
        userDropdownRef.current && 
        !userDropdownRef.current.contains(event.target as Node) &&
        userDropdownButtonRef.current && 
        !userDropdownButtonRef.current.contains(event.target as Node)
      ) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    AuthService.logout();
    setUser(null);
    setIsUserDropdownOpen(false);
    navigate('/');
  };

  const getHeaderBackground = () => {
    if (isMobile || location.pathname !== '/') {
      return 'bg-[#2b603e]';
    }
    return isScrolled 
      ? 'bg-[#2b603e]/80 backdrop-blur-md shadow-md' 
      : 'bg-transparent';
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${getHeaderBackground()}`}
    >
      <div className="container mx-auto px-4 py-4 flex flex-wrap justify-between items-center">
        {/* Logo */}
        <div className="flex items-center space-x-3">
          <img 
            src={require('../assets/images/logowithoutlogo.png')} 
            alt="Asclepius Logo" 
            className="h-10 w-10 rounded-full object-cover" 
          />
          <Link 
            to="/" 
            className="text-2xl md:text-3xl font-bold text-white tracking-wider brand-text"
          >
            Asclepius Shilajit
          </Link>
        </div>
        
        {/* Navigation */}
        <nav className="hidden md:flex items-center justify-center gap-6 relative">
          <Link 
            to="/" 
            className="text-white hover:text-[#C2C764] text-base uppercase tracking-wider font-semibold transition-colors duration-300"
            onClick={() => setIsProductDropdownOpen(false)}
          >
            Home
          </Link>
          
          {/* Products Dropdown */}
          <div 
            className="relative"
            ref={dropdownRef}
          >
            <div className="flex items-center">
              <Link 
                to="/products"
                className="text-white hover:text-[#C2C764] text-base uppercase tracking-wider font-semibold transition-colors duration-300"
                onClick={() => setIsProductDropdownOpen(false)}
              >
                Products
              </Link>
              <button 
                ref={dropdownButtonRef}
                onClick={() => {
                  if (isMobile) {
                    setIsProductDropdownOpen(!isProductDropdownOpen);
                  } else {
                    setIsProductDropdownOpen(true);
                  }
                }}
                onMouseEnter={() => {
                  if (!isMobile) {
                    setIsProductDropdownOpen(true);
                  }
                }}
                className="text-white hover:text-[#C2C764] ml-1 transition-colors duration-300"
              >
                <FontAwesomeIcon 
                  icon={faChevronDown} 
                  className="text-xs" 
                />
              </button>
            </div>
            {isProductDropdownOpen && (
              <div 
                ref={dropdownRef}
                onMouseLeave={() => {
                  if (!isMobile) {
                    setIsProductDropdownOpen(false);
                  }
                }}
                className={`${
                  isMobile 
                    ? 'bg-white/10 rounded-lg py-6 mt-2 space-y-8' 
                    : 'absolute top-full -left-80 mt-4 w-[800px] bg-white rounded-lg shadow-2xl py-6 z-50 grid grid-cols-2 gap-6 p-8'
                }`}
              >
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2 text-left">Our Products</h3>
                  <ul className="space-y-3">
                    <li>
                      <Link 
                        to="/products"
                        className="text-[#2b603e] hover:text-[#1a4a2a] hover:bg-gray-100 px-3 py-2 rounded-md flex items-center justify-between group text-left font-semibold"
                        onClick={() => setIsProductDropdownOpen(false)}
                      >
                        <span className="flex items-center">
                          View All Products ({products.length})
                        </span>
                        <FontAwesomeIcon 
                          icon={faChevronDown} 
                          className="text-xs text-gray-400 transform group-hover:rotate-180 transition-transform" 
                        />
                      </Link>
                    </li>
                    {Object.entries(productsByCategory).map(([category, categoryProducts]) => {
                      if (category === 'other') return null;
                      const categoryName = category.charAt(0).toUpperCase() + category.slice(1);
                      return (
                        <li key={category}>
                          <Link 
                            to={`/products?category=${category}`}
                            className="text-gray-700 hover:text-[#2b603e] hover:bg-gray-100 px-3 py-2 rounded-md flex items-center justify-between group text-left"
                            onClick={() => setIsProductDropdownOpen(false)}
                          >
                            <span className="flex items-center">
                              {categoryName} ({categoryProducts.length})
                            </span>
                            <FontAwesomeIcon 
                              icon={faChevronDown} 
                              className="text-xs text-gray-400 transform group-hover:rotate-180 transition-transform" 
                            />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Subscription Products Column */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2 text-left">Subscription Products</h3>
                  <ul className="space-y-3">
                    <li>
                      <Link 
                        to="/products?type=subscription"
                        className="text-gray-700 hover:text-[#2b603e] hover:bg-gray-100 px-3 py-2 rounded-md flex items-center justify-between group text-left"
                        onClick={() => setIsProductDropdownOpen(false)}
                      >
                        <span>All Subscriptions ({subscriptionProducts.length})</span>
                        <FontAwesomeIcon 
                          icon={faChevronDown} 
                          className="text-xs text-gray-400 transform group-hover:rotate-180 transition-transform" 
                        />
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <Link 
            to="/lab-tests" 
            className="text-white hover:text-[#C2C764] text-base uppercase tracking-wider font-semibold transition-colors duration-300"
            onClick={() => setIsProductDropdownOpen(false)}
          >
            Certification
          </Link>
          <Link 
            to="/about" 
            className="text-white hover:text-[#C2C764] text-base uppercase tracking-wider font-semibold transition-colors duration-300"
            onClick={() => setIsProductDropdownOpen(false)}
          >
            About Us
          </Link>
          <Link 
            to="/contact" 
            className="text-white hover:text-[#C2C764] text-base uppercase tracking-wider font-semibold transition-colors duration-300"
            onClick={() => setIsProductDropdownOpen(false)}
          >
            Contact
          </Link>
        </nav>
        
        {/* Search and Cart */}
        <div className="hidden md:flex items-center justify-center space-x-4">
          {/* Search */}
          <div className="relative">
            <input 
              type="text" 
              placeholder="Search products..." 
              className="bg-white/10 text-white placeholder-white/70 rounded-full py-2 px-4 pr-10 focus:outline-none focus:ring-2 focus:ring-[#C2C764] w-64 transition-all duration-300" 
            />
            <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition-colors duration-300">
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </div>

          {/* User Profile/Login */}
          {user ? (
            <div className="relative" ref={userDropdownRef}>
              <button
                ref={userDropdownButtonRef}
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                className="text-white hover:text-[#C2C764] transition-colors duration-300 flex items-center space-x-2"
              >
                <FontAwesomeIcon icon={faUserCircle} className="text-xl" />
                <span className="text-sm font-medium">{user.firstName}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
              
              {isUserDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setIsUserDropdownOpen(false)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <FontAwesomeIcon icon={faUserCircle} />
                    <span>My Profile</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link 
              to="/auth" 
              className="text-white hover:text-[#C2C764] transition-colors duration-300"
            >
              <FontAwesomeIcon icon={faUser} className="text-xl" />
            </Link>
          )}

          {/* Cart */}
          <Link 
            to="/checkout" 
            className="text-white hover:text-[#C2C764] transition-colors duration-300 relative"
          >
            <FontAwesomeIcon icon={faShoppingCart} className="text-xl" />
            {cart.totalItems > 0 && (
              <span 
                className="absolute -top-2 -right-2 bg-[#C2C764] text-[#2b603e] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
              >
                {cart.totalItems}
              </span>
            )}
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="block md:hidden">
          <button 
            onClick={toggleMenu} 
            className="text-white focus:outline-none bg-white/10 p-3 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
          >
            <FontAwesomeIcon 
              icon={isMenuOpen ? faTimes : faBars} 
              className="text-2xl" 
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-[#2b603e] z-50 overflow-y-auto">
          <div className="max-w-md mx-auto px-4 py-6 space-y-6">
            {/* Top bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img 
                  src={require('../assets/images/logowithoutlogo.png')} 
                  alt="Asclepius Logo" 
                  className="h-10 w-10 rounded-full object-cover" 
                />
                <span className="text-xl font-bold text-white brand-text">Asclepius</span>
              </div>
              <div className="flex items-center space-x-4">
                <Link 
                  to="/checkout" 
                  className="text-white relative"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FontAwesomeIcon icon={faShoppingCart} className="text-xl" />
                  {cart.totalItems > 0 && (
                    <span 
                      className="absolute -top-2 -right-2 bg-[#C2C764] text-[#2b603e] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                    >
                      {cart.totalItems}
                    </span>
                  )}
                </Link>
                <button 
                  onClick={() => setIsMenuOpen(false)} 
                  className="text-white focus:outline-none bg-white/10 p-2 rounded-lg hover:bg-white/20 transition-colors duration-300"
                  aria-label="Close menu"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xl" />
                </button>
              </div>
            </div>

            {/* User block */}
            <div className="bg-white/10 rounded-xl p-4 flex items-center justify-between">
              {user ? (
                <>
                  <div className="text-white">
                    <p className="text-sm font-semibold">{user.firstName} {user.lastName}</p>
                    <p className="text-xs text-white/70">{user.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="text-white hover:text-[#C2C764] transition-colors duration-300 flex items-center space-x-2"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    <span className="text-sm font-semibold">Sign out</span>
                  </button>
                </>
              ) : (
                <Link 
                  to="/auth" 
                  className="text-white font-semibold hover:text-[#C2C764] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign in / Create account
                </Link>
              )}
            </div>

            {/* Nav links */}
            <nav className="space-y-2">
              <Link 
                to="/" 
                className="block w-full text-white bg-white/5 hover:bg-white/10 rounded-lg px-4 py-3 text-lg font-semibold transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/products" 
                className="block w-full text-white bg-white/5 hover:bg-white/10 rounded-lg px-4 py-3 text-lg font-semibold transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Shop All Products
              </Link>
              <Link 
                to="/products?type=subscription" 
                className="block w-full text-white bg-white/5 hover:bg-white/10 rounded-lg px-4 py-3 text-lg font-semibold transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Subscriptions
              </Link>
              <Link 
                to="/lab-tests" 
                className="block w-full text-white bg-white/5 hover:bg-white/10 rounded-lg px-4 py-3 text-lg font-semibold transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Certification
              </Link>
              <Link 
                to="/about" 
                className="block w-full text-white bg-white/5 hover:bg-white/10 rounded-lg px-4 py-3 text-lg font-semibold transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>
              <Link 
                to="/contact" 
                className="block w-full text-white bg-white/5 hover:bg-white/10 rounded-lg px-4 py-3 text-lg font-semibold transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              {user && (
                <Link 
                  to="/profile" 
                  className="block w-full text-white bg-white/5 hover:bg-white/10 rounded-lg px-4 py-3 text-lg font-semibold transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  My Profile
                </Link>
              )}
              <Link 
                to="/checkout" 
                className="block w-full text-white bg-white/5 hover:bg-white/10 rounded-lg px-4 py-3 text-lg font-semibold transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Checkout
              </Link>
            </nav>

            {/* Social */}
            <div className="pt-2 flex justify-center space-x-6">
              <a href="https://www.instagram.com/asclepius_shilajit" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#C2C764] transition-colors duration-300">
                <FontAwesomeIcon icon={faInstagram} size="2x" />
              </a>
              <a href="https://www.tiktok.com/@asclepiusshilajit" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#C2C764] transition-colors duration-300">
                <FontAwesomeIcon icon={faTiktok} size="2x" />
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;